type StudentScholarshipData = App.Students.Data.StudentScholarshipData;
type ApplicationData = App.Students.Data.ApplicationData;
type TestData = App.Students.Data.TestData;
type FinancialAidData = App.Students.Data.FinancialAidData;
type DataRequestData = App.DataRequests.Data.DataRequestData;

export function useDataRequests(
  type: 'student_scholarship' | 'application' | 'test' | 'financial_aid'
) {
  function getEditDataRequestRoute(
    item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData
  ): string {
    const dataRequest: DataRequestData | null | undefined =
      'requestable_type' in item ? item : item?.pending_data_request;

    if (!dataRequest) {
      return '';
    }

    switch (type) {
      case 'student_scholarship':
        return route('student.data-requests.scholarship.edit', {
          dataRequest: dataRequest?.id
        });
      case 'application':
        return route('student.data-requests.college-application.edit', {
          dataRequest: dataRequest?.id
        });
      case 'test':
        return route('student.data-requests.test.edit', {
          dataRequest: dataRequest?.id
        });
      case 'financial_aid':
        return route('student.data-requests.financial-aid.edit', {
          dataRequest: dataRequest?.id
        });
      default:
        return '';
    }
  }

  function getEditRoute(
    item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData
  ): string {
    switch (type) {
      case 'student_scholarship':
        return route('student.data-requests.edit-scholarship.create', {
          scholarship: item.id
        });
      case 'application':
        return route('student.data-requests.edit-college-application.create', {
          application: item.id
        });
      case 'test':
        return route('student.data-requests.edit-test.create', {
          test: item.id
        });
      case 'financial_aid':
        return route('student.data-requests.edit-financial-aid.create', {
          financialAid: item.id
        });
      default:
        return '';
    }
  }

  function getCreateFromRejectedRoute(
    item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData
  ): string {
    const dataRequest: DataRequestData | null | undefined =
      'requestable_type' in item ? item : item.undismissed_rejected_data_request;

    if (!dataRequest) {
      return '';
    }

    switch (type) {
      case 'student_scholarship':
        return route('student.data-requests.scholarship-from-rejected.create', {
          dataRequest: dataRequest.id
        });
      case 'application':
        return route('student.data-requests.college-application-from-rejected.create', {
          dataRequest: dataRequest.id
        });
      case 'test':
        return route('student.data-requests.test-from-rejected.create', {
          dataRequest: dataRequest.id
        });
      case 'financial_aid':
        return route('student.data-requests.financial-aid-from-rejected.create', {
          dataRequest: dataRequest.id
        });
      default:
        return '';
    }
  }

  const createRoute = computed(() => {
    switch (type) {
      case 'student_scholarship':
        return route('student.data-requests.create-scholarship.create');
      case 'application':
        return route('student.data-requests.create-college-application.create');
      case 'test':
        return route('student.data-requests.create-test.create');
      default:
        return null;
    }
  });

  function getRoute(
    item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData
  ): string {
    if ('requestable_type' in item) {
      return handleDataRequest(item);
    }

    if (item.pending_data_request?.id) {
      return getEditDataRequestRoute(item);
    }
    if (item.undismissed_rejected_data_request?.id) {
      return getCreateFromRejectedRoute(item);
    }
    return getEditRoute(item);
  }

  function handleDataRequest(dataRequest: DataRequestData): string {
    if (dataRequest.status.value === 'pending') {
      return getEditDataRequestRoute(dataRequest);
    }
    if (dataRequest.status.value === 'rejected') {
      return getCreateFromRejectedRoute(dataRequest);
    }
    return '';
  }

  return {
    getRoute,
    getCreateFromRejectedRoute,
    createRoute
  };
}
